import { useNavigate } from "@tanstack/react-router";
import { ArrowDownUp, EyeOff, Package, SearchCode, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { CategoryTag } from "@/components/skills/CategoryTag";
import { RiskBadge } from "@/components/skills/RiskBadge";
import { StatCard } from "@/components/skills/StatCard";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useModeration, useModerationPublish } from "@/hooks/use-moderation";
import { filterModerationRows, type ModerationTab, sortRowsByRisk } from "@/lib/skills/moderation";

export function ModerationConsole() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ModerationTab>("all");
  const [sortRisk, setSortRisk] = useState(false);
  const { data, isLoading, isError, error, refetch } = useModeration();
  const publish = useModerationPublish();

  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-[12px] bg-bg-2" />;
  }
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Could not load moderation data"}
        onRetry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  const { rows, stats } = data;
  let visible = filterModerationRows(rows, tab);
  if (sortRisk) visible = sortRowsByRisk(visible);

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <div className="text-xs font-semibold uppercase tracking-wide text-fg-link">Govern</div>
        <h1 className="font-display text-3xl font-bold text-navy">Moderation Console</h1>
        <p className="max-w-2xl text-sm text-fg-2">
          Keep the library safe, current, and high-quality — review risk findings, manage ownership,
          and unpublish problematic skills without blocking contribution.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Package}
          label="Published"
          value={String(stats.published)}
          caption={
            stats.newThisWeek > 0 ? `${stats.newThisWeek} new this week` : "no new this week"
          }
          up={stats.newThisWeek > 0}
        />
        <StatCard
          icon={ShieldAlert}
          label="Needs review"
          value={String(stats.flagged)}
          caption={`${stats.flagged} flagged`}
        />
        <StatCard
          icon={SearchCode}
          label="Open findings"
          value={String(stats.openFindings)}
          caption={`across ${stats.skillsWithFindings} skills`}
        />
        <StatCard
          icon={EyeOff}
          label="Unpublished"
          value={String(stats.unpublished)}
          caption="hidden from users"
        />
      </div>

      <div className="overflow-hidden rounded-[12px] border border-border-1 bg-bg-1 shadow-xs">
        <div className="flex flex-col gap-3 border-b border-border-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <ToggleGroup
            type="single"
            value={tab}
            onValueChange={(v) => {
              if (v === "all" || v === "flagged" || v === "unpublished") setTab(v);
            }}
            className="bg-bg-3"
          >
            <ToggleGroupItem value="all">All skills</ToggleGroupItem>
            <ToggleGroupItem value="flagged">Needs review · {stats.flagged}</ToggleGroupItem>
            <ToggleGroupItem value="unpublished">Unpublished · {stats.unpublished}</ToggleGroupItem>
          </ToggleGroup>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSortRisk((s) => !s)}
            aria-pressed={sortRisk}
          >
            <ArrowDownUp className="size-4" />
            {sortRisk ? "Sorted by risk" : "Sort by risk"}
          </Button>
        </div>

        {visible.length === 0 ? (
          <p className="px-6 py-8 text-sm text-fg-3">No skills in this view.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skill</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead className="text-right">Findings</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Version</TableHead>
                <TableHead className="text-right">Downloads</TableHead>
                <TableHead className="text-right">Published</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((row) => (
                <TableRow key={row.id} className={row.published ? undefined : "opacity-60"}>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => void navigate({ to: "/skill/$id", params: { id: row.slug } })}
                      className="font-medium text-navy hover:text-fg-link"
                    >
                      {row.title}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {row.cat.map((c) => (
                        <CategoryTag key={c} category={c} />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <RiskBadge level={row.risk} />
                  </TableCell>
                  <TableCell className="text-right">
                    {row.openFindings > 0 ? (
                      <span
                        className={
                          row.risk === "high"
                            ? "font-bold tabular-nums text-danger"
                            : "font-bold tabular-nums text-warning"
                        }
                      >
                        {row.openFindings}
                      </span>
                    ) : (
                      <span className="text-fg-3">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-fg-2">{row.ownerName}</TableCell>
                  <TableCell className="tabular-nums text-fg-3">{row.version}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.downloads.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={row.published}
                      disabled={publish.isPending}
                      onCheckedChange={(checked) =>
                        publish.mutate({ slug: row.slug, title: row.title, published: checked })
                      }
                      aria-label={
                        row.published ? `Unpublish ${row.title}` : `Re-publish ${row.title}`
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
